import { useMemo, useState, type ChangeEvent } from "react";
import { Form, Pagination, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAuditLog } from "../../hooks/queries/useAuditLog";
import { AUDIT_ACTIONS, type AuditLogEntry } from "../../types/domain";
import { formatDate, formatTime } from "../../util/StringUtils";

const PAGE_SIZE = 25;

/**
 * Converts a YYYY-MM-DD value from a <Form.Control type="date"> into an ISO datetime
 * for the backend. Empty string → null (no filter).
 */
const dateInputToIsoStart = (value: string): string | null =>
  value ? new Date(`${value}T00:00:00`).toISOString() : null;

const dateInputToIsoEnd = (value: string): string | null =>
  value ? new Date(`${value}T23:59:59.999`).toISOString() : null;

function AuditLogTab() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [action, setAction] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const filters = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      action: action || null,
      username: username.trim() || null,
      from: dateInputToIsoStart(fromDate),
      to: dateInputToIsoEnd(toDate),
    }),
    [page, action, username, fromDate, toDate],
  );

  const { data, isFetching } = useAuditLog(filters);

  // Reset to page 0 whenever any filter changes. React Bootstrap's onChange uses a wider
  // element union (includes textarea), so accept any HTMLElement with a `.value` here.
  const updateFilter = (setter: (v: string) => void) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setter(event.target.value);
    setPage(0);
  };

  const totalPages = data?.totalPages ?? 0;
  const entries: AuditLogEntry[] = data?.content ?? [];

  return (
    <div className="mt-3">
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Form.Select
          value={action}
          onChange={updateFilter(setAction)}
          style={{ maxWidth: "260px" }}
        >
          <option value="">{t("auditLog__allActions")}</option>
          {AUDIT_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {t(`auditAction__${a}`, { defaultValue: a })}
            </option>
          ))}
        </Form.Select>

        <Form.Control
          type="text"
          placeholder={t("auditLog__usernameFilter")}
          value={username}
          onChange={updateFilter(setUsername)}
          style={{ maxWidth: "200px" }}
        />

        <Form.Control
          type="date"
          value={fromDate}
          onChange={updateFilter(setFromDate)}
          style={{ maxWidth: "180px" }}
          aria-label={t("auditLog__from")}
        />

        <Form.Control
          type="date"
          value={toDate}
          onChange={updateFilter(setToDate)}
          style={{ maxWidth: "180px" }}
          aria-label={t("auditLog__to")}
        />

        {isFetching && <Spinner animation="border" size="sm" className="align-self-center" />}
      </div>

      <Table striped bordered hover size="sm" variant="dark" responsive>
        <thead>
          <tr>
            <th style={{ width: "11rem" }}>{t("auditLog__when")}</th>
            <th style={{ width: "10rem" }}>{t("auditLog__user")}</th>
            <th style={{ width: "16rem" }}>{t("auditLog__action")}</th>
            <th style={{ width: "10rem" }}>{t("auditLog__entity")}</th>
            <th>{t("auditLog__details")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && !isFetching ? (
            <tr>
              <td colSpan={5} className="text-center" style={{ color: "var(--text-muted)" }}>
                {t("auditLog__empty")}
              </td>
            </tr>
          ) : (
            entries.map((entry) => (
              <tr key={entry.id}>
                <td>
                  {formatDate(entry.createdAt)} {formatTime(entry.createdAt)}
                </td>
                <td>{entry.username ?? "-"}</td>
                <td>{t(`auditAction__${entry.action}`, { defaultValue: entry.action })}</td>
                <td>
                  {entry.entityType ? (
                    <>
                      {entry.entityType}
                      {entry.entityId != null ? ` #${entry.entityId}` : ""}
                    </>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  <DetailsCell raw={entry.details} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination>
            <Pagination.First onClick={() => setPage(0)} disabled={page === 0} />
            <Pagination.Prev onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} />
            <Pagination.Item active>{page + 1}</Pagination.Item>
            <Pagination.Next
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            />
            <Pagination.Last
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
}

/**
 * Renders the JSON `details` blob as a list of key: value pairs when it parses as a flat object.
 * Falls back to the raw string otherwise.
 */
function DetailsCell({ raw }: { raw: string | null }) {
  if (!raw) return <span style={{ color: "var(--text-muted)" }}>-</span>;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return (
        <div style={{ fontSize: "0.85em" }}>
          {Object.entries(parsed).map(([k, v]) => (
            <div key={k}>
              <span style={{ color: "var(--text-muted)" }}>{k}:</span>{" "}
              <span>{String(v)}</span>
            </div>
          ))}
        </div>
      );
    }
  } catch {
    // Not JSON, fall through.
  }
  return <span style={{ fontSize: "0.85em" }}>{raw}</span>;
}

export default AuditLogTab;
